"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X } from "lucide-react";
import { FaTelegram } from "react-icons/fa";

interface ErrorDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  errorMessage?: string;
  cancelText?: string;
  actionText?: string;
  showSupportLink?: boolean;
  supportInfo?: string;
  accountId?: string;
  onAction?: () => void;
  onCancel?: () => void;
}

export function ErrorDialog({
  isOpen,
  onOpenChange,
  title = "Error Occurred",
  description = "We encountered an issue while processing your request.",
  errorMessage,
  cancelText,
  actionText = "Try Again",
  showSupportLink = true,
  supportInfo,
  accountId,
  onAction,
  onCancel,
}: ErrorDialogProps) {
  
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  const handleAction = () => {
    if (onAction) {
      onAction();
    }
    onOpenChange(false);
  };

  const openTelegramSupport = () => {
    window.open('https://t.me/batchfinderdev', '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <span className="text-destructive">{title}</span>
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <div className="my-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            {errorMessage}
          </div>
        )}

        {showSupportLink && (
          <div className="my-4 space-y-3">
            <p className="text-sm">If the problem persists, please contact our support team:</p>
            <Button 
              variant="outline" 
              className="w-full flex items-center justify-center gap-2 text-sm"
              onClick={openTelegramSupport}
            >
              <FaTelegram className="h-4 w-4 text-[#229ED9]" />
              Contact Support on Telegram
            </Button>
            
            {accountId && (
              <p className="text-xs text-muted-foreground">
                Please include your account ID when contacting support: 
                <span className="font-mono bg-muted px-1 rounded ml-1">
                  {accountId.length > 8 ? `${accountId.substring(0, 8)}...` : accountId}
                </span>
              </p>
            )}
            
            {supportInfo && (
              <p className="text-xs text-muted-foreground">
                {supportInfo}
              </p>
            )}
          </div>
        )}
        
        <DialogFooter className="gap-2 sm:gap-0">
          {cancelText && (
            <Button 
              variant="outline" 
              onClick={handleCancel}
              className="mt-0"
            >
              {cancelText}
            </Button>
          )}
          <Button 
            onClick={handleAction}
            className="bg-destructive hover:bg-destructive/90"
          >
            {actionText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 